import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Path } from '../../router/BaseRouter/enums'
import { getLessonRequests } from '../../service/lesson-requests.service'
import { LessonRequestDto, RequestStatus } from '../../service/types'
import { RequestDialog } from './RequestDialog'

const statusColorMap: Record<RequestStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  [RequestStatus.New]: 'info',
  [RequestStatus.InProgress]: 'warning',
  [RequestStatus.Done]: 'success',
  [RequestStatus.Rejected]: 'error',
}

const RESOLVED_STATUSES: RequestStatus[] = [RequestStatus.Done, RequestStatus.Rejected]

export const Requests: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [requests, setRequests] = useState<LessonRequestDto[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState<LessonRequestDto | null>(null)
  const [showResolved, setShowResolved] = useState(false)

  const filteredRequests = useMemo(
    () => (showResolved ? requests : requests.filter((r) => !RESOLVED_STATUSES.includes(r.status))),
    [requests, showResolved],
  )

  const loadRequests = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getLessonRequests()
      setRequests(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadRequests()
  }, [loadRequests])

  const handleAdd = () => {
    setSelectedRequest(null)
    setDialogOpen(true)
  }

  const handleRowClick = (request: LessonRequestDto) => {
    setSelectedRequest(request)
    setDialogOpen(true)
  }

  const handleConvertToLesson = (request: LessonRequestDto) => {
    const params = new URLSearchParams({
      date: request.date,
      time: request.time,
      customerId: request.customerId,
      requestId: request.id,
      requestSummary: JSON.stringify({
        customerName: request.customer?.name,
        lessonType: request.lessonType,
        discipline: request.discipline,
        level: request.level,
        skiers: request.skiers,
        notes: request.notes,
      }),
    })
    navigate(`${Path.DAY_PLAN}?${params.toString()}`)
  }

  return (
    <ScreenWrapper
      title={t('requests.title')}
      filter={
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Switch checked={showResolved} onChange={(_, checked) => setShowResolved(checked)} size="small" />}
            label={t('requests.showResolved')}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('requests.addRequest')}
          </Button>
        </Box>
      }
    >
      {loading && <LinearProgress sx={{ mt: 2 }} />}
      <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('requests.form.status')}</TableCell>
              <TableCell>{t('requests.form.date')}</TableCell>
              <TableCell>{t('requests.form.time')}</TableCell>
              <TableCell>{t('requests.form.customer')}</TableCell>
              <TableCell>{t('requests.form.lessonType')}</TableCell>
              <TableCell>{t('requests.form.discipline')}</TableCell>
              <TableCell>{t('requests.form.level')}</TableCell>
              <TableCell>{t('requests.form.skiers')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => (
              <TableRow key={request.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(request)}>
                <TableCell>
                  <Chip
                    label={t(`requestStatus.${request.status}`)}
                    color={statusColorMap[request.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>{request.date}</TableCell>
                <TableCell>{request.time}</TableCell>
                <TableCell>{request.customer?.name ?? '\u2014'}</TableCell>
                <TableCell>{t(`lessonType.${request.lessonType}`)}</TableCell>
                <TableCell>{t(`discipline.${request.discipline}`)}</TableCell>
                <TableCell>{t(`skierLevel.${request.level}`)}</TableCell>
                <TableCell>{request.skiers.length}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <RequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void loadRequests()}
        onConvertToLesson={handleConvertToLesson}
        request={selectedRequest}
      />
    </ScreenWrapper>
  )
}
